import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { upperFirst } from 'lodash';

import { buildTransitionEventView } from '@satellite/helpers/yardTaskTransitionEvents';
import {
  formatPersonName,
  formatTaskDate,
  formatTaskNumber,
  getTaskStatusIcon,
  getTaskStatusColor,
  getTaskStatusBackgroundColor,
  isTaskCancelled,
  isTaskCompleted,
  isTaskInProgress,
  isTaskIssue,
  isTaskUrgent,
  mapTags
} from '@satellite/helpers/yardTaskUtils';
import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { reactiveOption } from '@satellite/composables/presenterReactiveOption';

import { YardTaskStatus } from '@nova/core';

import type { ComputedRef, Ref } from 'vue';
import type {
  PresenterAttrsParams,
  PresenterDisplayConfigOptions,
  PresenterDisplayOverrideProps,
  PresenterDisplaySize
} from '@satellite/composables/presenterDisplayOptions';
import type { YardTaskDetails } from './yardTaskPresenterShared';
import type { YardTaskPresenterBaseDisplayField } from './yardTaskPresenterTypes';

export type YardTaskDisplayField = YardTaskPresenterBaseDisplayField;

export interface UseYardTaskDisplayOptions extends PresenterDisplayConfigOptions {
  typeLabelProps?: PresenterDisplayOverrideProps;
  taskNumberProps?: PresenterDisplayOverrideProps;
  urgentProps?: PresenterDisplayOverrideProps;
  descriptionProps?: PresenterDisplayOverrideProps;
  assigneeProps?: PresenterDisplayOverrideProps;
  dueDateProps?: PresenterDisplayOverrideProps;
  tagsProps?: PresenterDisplayOverrideProps;
  statusProps?: PresenterDisplayOverrideProps;
  statusChipProps?: PresenterDisplayOverrideProps;
  issueProps?: PresenterDisplayOverrideProps;
  elapsedTimeProps?: PresenterDisplayOverrideProps;
}

const STATUS_LABEL_TRANSLATION_KEYS = {
  [YardTaskStatus.CREATED]: 'global.tasks.presenter.status.toDo',
  [YardTaskStatus.IN_PROGRESS]: 'global.tasks.presenter.status.inProgress',
  [YardTaskStatus.COMPLETED]: 'global.tasks.presenter.status.completed',
  [YardTaskStatus.CANCELLED]: 'global.tasks.presenter.status.canceled'
};

export function useYardTaskDisplay(
  task: Ref<YardTaskDetails | null> | ComputedRef<YardTaskDetails | null>,
  options: UseYardTaskDisplayOptions = {}
) {
  const { t } = useI18n();

  const displaySize = reactiveOption<PresenterDisplaySize>(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterMode.Live);
  const effectiveTimezone = computed(() => toValue(options.warehouseTimezone) ?? 'utc');
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));

  const typeLabel = computed(() => {
    const currentTask = task.value;

    if (!currentTask?.type) {
      return '';
    }

    return upperFirst((currentTask as YardTaskDetails).type);
  });

  const taskNumber = computed(() => formatTaskNumber(task.value?.refNum));

  const description = computed(() => task.value?.description ?? null);

  const assigneeName = computed(() => {
    const assignedTo = task.value?.assignedTo;
    if (!assignedTo) {
      return t('global.tasks.presenter.unassigned');
    }

    return formatPersonName(assignedTo) || t('global.tasks.presenter.unassigned');
  });

  const dueDateText = computed(() => {
    const due = task.value?.dueDateTime ?? null;
    if (!due) {
      return t('global.tasks.presenter.noDueDate');
    }
    return (
      formatTaskDate(due, 'MMM d, yyyy @ h:mm a', effectiveTimezone.value) ||
      t('global.tasks.presenter.noDueDate')
    );
  });

  const tags = computed(() => mapTags(task.value?.tags));

  const status = computed(() => (task.value?.status as YardTaskStatus) ?? null);

  const statusIcon = computed(() => getTaskStatusIcon(status.value));

  const statusLabel = computed(() => {
    const currentStatus = status.value;

    if (!currentStatus) {
      return '';
    }

    if (
      STATUS_LABEL_TRANSLATION_KEYS[currentStatus as keyof typeof STATUS_LABEL_TRANSLATION_KEYS]
    ) {
      return t(
        STATUS_LABEL_TRANSLATION_KEYS[currentStatus as keyof typeof STATUS_LABEL_TRANSLATION_KEYS]
      );
    }

    return currentStatus;
  });

  const statusIconColor = computed(() => getTaskStatusColor(status.value));

  const statusChipColor = computed(() => getTaskStatusBackgroundColor(status.value));

  const isUrgent = computed(() => isTaskUrgent(task.value));

  const hasIssue = computed(() => isTaskIssue(task.value));

  const inProgressTransition = computed(() =>
    buildTransitionEventView(task.value?.events, 'in-progress', {
      warehouseTimezone: effectiveTimezone.value
    })
  );

  const completedTransition = computed(() =>
    buildTransitionEventView(task.value?.events, 'completed', {
      warehouseTimezone: effectiveTimezone.value
    })
  );

  const cancelledTransition = computed(() =>
    buildTransitionEventView(task.value?.events, 'cancelled', {
      warehouseTimezone: effectiveTimezone.value
    })
  );

  const elapsedTimeRender = computed(() => {
    if (!task.value) {
      return null;
    }

    const inProgressEvent = inProgressTransition.value;
    const completedEvent = completedTransition.value;
    const cancelledEvent = cancelledTransition.value;

    if (isTaskCompleted(task.value) && inProgressEvent?.rawAt && completedEvent?.rawAt) {
      return {
        from: inProgressEvent.rawAt,
        until: completedEvent.rawAt,
        updateInterval: 0,
        variant: 'human' as const
      };
    }

    if (isTaskCancelled(task.value) && inProgressEvent?.rawAt && cancelledEvent?.rawAt) {
      return {
        from: inProgressEvent.rawAt,
        until: cancelledEvent.rawAt,
        updateInterval: 0,
        variant: 'human' as const
      };
    }

    if (isTaskInProgress(task.value) && inProgressEvent?.rawAt) {
      return {
        from: inProgressEvent.rawAt,
        until: null,
        updateInterval: 60_000,
        variant: 'human' as const
      };
    }

    return null;
  });

  const typeLabelAttrs = ({ size = displaySize.value }: PresenterAttrsParams = {}) =>
    mergeProps(
      {
        class: {
          'typography-heading-sm-bold': size === 'small',
          'typography-heading-md-bold': size === 'regular',
          'typography-heading-lg-bold': size === 'large'
        }
      },
      toValue(options.typeLabelProps) ?? {}
    );

  const taskNumberAttrs = ({ size = displaySize.value }: PresenterAttrsParams = {}) =>
    mergeProps(
      {
        class: {
          'd-flex flex-row gap-0-5 align-center': true,
          'typography-caption-bold': size === 'small',
          'typography-body-sm-bold': size === 'regular',
          'typography-body-md-bold': size === 'large'
        }
      },
      toValue(options.taskNumberProps) ?? {}
    );

  const descriptionAttrs = computed(() =>
    mergeProps({ style: 'white-space: pre-line;' }, toValue(options.descriptionProps) ?? {})
  );

  const assigneeAttrs = ({ size = displaySize.value }: PresenterAttrsParams = {}) =>
    mergeProps(
      {
        class: {
          'typography-caption-bold': size === 'small',
          'typography-body-sm-bold': size === 'regular',
          'typography-body-md-bold': size === 'large'
        }
      },
      toValue(options.assigneeProps) ?? {}
    );

  const dueDateAttrs = ({ size = displaySize.value }: PresenterAttrsParams = {}) =>
    mergeProps(
      {
        class: {
          'typography-caption-bold': size === 'small',
          'typography-body-sm-bold': size === 'regular',
          'typography-body-md-bold': size === 'large'
        }
      },
      toValue(options.dueDateProps) ?? {}
    );

  const tagChipTextAttrs = ({ size = displaySize.value }: PresenterAttrsParams = {}) =>
    mergeProps(
      {
        class: {
          'typography-chip-sm': size === 'small',
          'typography-caption-bold': ['regular', 'large'].includes(size)
        }
      },
      toValue(options.tagsProps) ?? {}
    );

  const tagsContainerAttrs = computed(() => mergeProps(toValue(options.tagsProps) ?? {}, {}));

  const statusTextAttrs = ({ size = displaySize.value }: PresenterAttrsParams = {}) =>
    mergeProps(
      {
        class: {
          'typography-caption-bold': size === 'small',
          'typography-body-sm-bold': size === 'regular',
          'typography-body-md-bold': size === 'large'
        }
      },
      toValue(options.statusProps) ?? {}
    );

  const statusChipTextAttrs = ({ size = displaySize.value }: PresenterAttrsParams = {}) =>
    mergeProps(
      {
        class: {
          'typography-chip-sm': size === 'small',
          'typography-caption-bold': ['regular', 'large'].includes(size)
        }
      },
      toValue(options.statusChipProps) ?? {}
    );

  const issueAttrs = ({ size = displaySize.value }: PresenterAttrsParams = {}) =>
    mergeProps(
      {
        class: {
          'typography-caption-bold': size === 'small',
          'typography-body-sm-bold': size === 'regular',
          'typography-body-md-bold': size === 'large',
          'text--color-error-60': hasIssue.value
        }
      },
      toValue(options.issueProps) ?? {}
    );

  const elapsedTimeAttrs = computed(() => mergeProps(toValue(options.elapsedTimeProps) ?? {}, {}));

  const urgentIconAttrs = computed(() => mergeProps(toValue(options.urgentProps) ?? {}, {}));

  return reactive({
    task,
    mode: displayMode,
    size: displaySize,
    warehouseTimezone: effectiveTimezone,
    isFetching: isFetchingValue,
    typeLabel,
    taskNumber,
    description,
    assigneeName,
    dueDateText,
    tags,
    status,
    statusIcon,
    statusLabel,
    statusIconColor,
    statusChipColor,
    isUrgent,
    hasIssue,
    elapsedTimeRender,
    typeLabelAttrs,
    taskNumberAttrs,
    descriptionAttrs,
    assigneeAttrs,
    dueDateAttrs,
    tagChipTextAttrs,
    tagsContainerAttrs,
    statusTextAttrs,
    statusChipTextAttrs,
    issueAttrs,
    elapsedTimeAttrs,
    urgentIconAttrs
  });
}

export type YardTaskDisplayData = ReturnType<typeof useYardTaskDisplay>;
